import {
  useState,
  FC,
  ReactNode,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  memo,
} from 'react';
import { Icon, tailwindOverride, Tooltip } from '@getsynapse/design-system';
import { EditableTableRowCell } from '../EditableTableRowCell';
import { BaseCellProps } from '../types';
import {
  InlineDropdown,
  DropdownOption,
} from '../InlineDropdown/InlineDropdown';
import { useTooltip } from '../hooks';

export type InlineDropdownOption = DropdownOption;

interface InlineDropdownTableRowCellProps extends Omit<BaseCellProps, 'value'> {
  value: string;
  customizedValue: (value: string) => ReactNode;
  options: InlineDropdownOption[];
  placeholder?: string;
  canEdit?: boolean;
  isDisabled?: boolean;
  disabledMessage?: string;
  className?: string;
  renderOption?: (
    option: InlineDropdownOption,
    selected: boolean,
    selectOption: () => void,
    otherProps: Record<string, any>
  ) => ReactNode;
  dataAttributePrefix?: string;
}

// This tooltip is portaled to <body> (usePortal) with popper positioning
// (showPopper) so it escapes the requests table's `overflow-auto` scroll
// container. Rendered inline (absolute) instead, it is clipped by that overflow
// and appears behind the sticky header when it opens upward from a top row
// (CGP-235). z-200000 matches the app convention for tooltips that must sit
// above the sticky header (see RequestsTableOperationHeader). `max-w-xs` (from
// the design system) with `whitespace-normal` keeps a long message wrapping to
// multiple lines rather than overflowing the box.
const TOOLTIP_CONTENT_PROPS = {
  className: 'px-3 py-2 text-sm z-200000 whitespace-normal break-words',
};

const InlineDropdownTableRowCellComponent: FC<
  InlineDropdownTableRowCellProps
> = ({
  value,
  customizedValue,
  options,
  isEditable,
  className = '',
  onSave,
  onCancel,
  canEdit = true,
  isDisabled = false,
  disabledMessage,
  renderOption,
  dataAttributePrefix = 'dropdown',
}) => {
  const [editValue, setEditValue] = useState(value);
  const toggleDropdownRef = useRef<(() => void) | null>(null);
  const dropdownTriggerRef = useRef<HTMLDivElement | null>(null);
  const saveTriggerRef = useRef<(() => void) | null>(null);
  const pendingSaveValueRef = useRef<string | null>(null);

  const { tooltipRef } = useTooltip();

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  );

  const hoverStateStyles = useMemo(
    () =>
      tailwindOverride(
        'border border-transparent bg-white hover:border-cool-mist group-focus-visible:border-cool-mist rounded flex items-center justify-between p-2 ml-1'
      ),
    []
  );

  const fieldChildren = useMemo(
    () => (
      <>
        {customizedValue(value)}
        <span title='' aria-hidden='true' className='inline-flex'>
          <Icon name='caret-down-outline' className='pointer-events-none' />
        </span>
      </>
    ),
    [customizedValue, value]
  );

  const hoverInputChildren = useMemo(() => {
    if (!canEdit || isDisabled) {
      return (
        <div
          className={tailwindOverride(
            'cursor-pointer',
            hoverStateStyles,
            'border-cool-mist bg-off-white'
          )}
          aria-disabled={true}
        >
          {fieldChildren}
        </div>
      );
    }
    return (
      <div className={tailwindOverride('cursor-pointer', hoverStateStyles)}>
        {fieldChildren}
      </div>
    );
  }, [canEdit, isDisabled, hoverStateStyles, fieldChildren]);

  const readModeChildren = useMemo(
    () => (
      <div className='border border-transparent p-2 ml-1'>
        {customizedValue(value)}
      </div>
    ),
    [customizedValue, value]
  );

  const handleSave = useCallback(() => {
    const valueToSave = pendingSaveValueRef.current ?? editValue;
    pendingSaveValueRef.current = null;
    onSave?.(valueToSave);
    // Snap the editor back to the source-of-truth `value`. onSave may defer the
    // real status change to a confirmation modal; if the user cancels that
    // modal, `value` never updates. Without this reset the editor would keep
    // showing the abandoned selection and re-fire it on the next save (e.g. an
    // outside-click), re-opening the modal on reopen (CGP-180). When the change
    // IS confirmed, the `value` prop updates and the useEffect above re-syncs.
    setEditValue(value);
  }, [editValue, onSave, value]);

  const handleCancel = useCallback(() => {
    setEditValue(value);
    pendingSaveValueRef.current = null;
    onCancel?.();
  }, [value, onCancel]);

  const onChange = useCallback((newValue: string) => {
    setEditValue(newValue);
    pendingSaveValueRef.current = newValue;
    saveTriggerRef.current?.();
  }, []);

  const handleStopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const tooltipMessage = isDisabled ? disabledMessage || '' : '';

  const editModeChildren = useMemo(
    () => (
      <InlineDropdown
        value={editValue}
        customizedValue={customizedValue}
        selectedOption={selectedOption ? [selectedOption] : []}
        options={options}
        onChange={onChange}
        toggleDropdownRef={toggleDropdownRef}
        triggerRef={dropdownTriggerRef}
        autoOpen
        saveTriggerRef={saveTriggerRef}
        renderOption={renderOption}
        dataAttributePrefix={dataAttributePrefix}
      />
    ),
    [
      editValue,
      customizedValue,
      selectedOption,
      options,
      onChange,
      renderOption,
      dataAttributePrefix,
    ]
  );

  const tooltipTrigger = (
    <div className={tooltipMessage ? 'w-fit' : 'w-full'}>
      <EditableTableRowCell
        isEditable={!!isEditable}
        editModeChildren={editModeChildren}
        readModeChildren={readModeChildren}
        hoverInputChildren={hoverInputChildren}
        showHoverInput={true}
        className={className}
        onSave={handleSave}
        onCancel={handleCancel}
        saveTriggerRef={saveTriggerRef}
      />
    </div>
  );

  return (
    <div
      role='group'
      tabIndex={-1}
      onClick={handleStopPropagation}
      onKeyDown={(e) =>
        (e.key === 'Enter' || e.key === ' ') && e.stopPropagation()
      }
      className={tooltipMessage ? 'w-fit' : 'w-full'}
    >
      <Tooltip
        ref={tooltipRef}
        openMode='hover1'
        timeout={0}
        showPopper
        position='topCenter'
        contentProps={TOOLTIP_CONTENT_PROPS}
        className='w-full'
        usePortal
        trigger={tooltipTrigger}
        ariaId='request-status-cell-tooltip'
      >
        {tooltipMessage}
      </Tooltip>
    </div>
  );
};

export const InlineDropdownTableRowCell = memo(
  InlineDropdownTableRowCellComponent
);
